import { Course } from '@/types/courseTypes';

export const aiPlatformIntegrationCourse: Course = {
  id: 'ai-platform-integration',
  title: 'Cross-Platform AI Integration',
  description: 'Build enterprise AI systems that integrate multiple providers, edge computing, and cloud platforms',
  level: 'advanced',
  duration: '22 hours',
  durationHours: 22,
  image: '/placeholder.svg',
  tags: ['Multi-Cloud AI', 'Enterprise Architecture', 'AI Integration', 'Hybrid Systems', 'MLOps'],
  prerequisites: ['Experience with multiple AI APIs', 'Cloud computing knowledge', 'System design understanding'],
  learningOutcomes: [
    'Design multi-provider AI architectures with fallback strategies',
    'Integrate NVIDIA GPU acceleration with cloud AI services',
    'Build GitHub-centric ML workflows for teams',
    'Implement production AI monitoring and observability',
    'Optimize costs across multiple AI providers'
  ],
  type: 'specialized-ai',
  modules: [
    {
      id: 'platform-module-1',
      title: 'Multi-Provider AI Architecture',
      description: 'Design systems that leverage multiple AI providers',
      content: `# Multi-Provider AI Architecture

## Why Multi-Provider Strategy?

### Benefits
- **Reliability**: Fallback when one provider has outages
- **Cost Optimization**: Route to cheapest provider for task
- **Feature Access**: Use best provider for each capability
- **Vendor Lock-in Avoidance**: Maintain flexibility

### Trade-offs
- Increased complexity
- Different API patterns
- Varied rate limits
- Inconsistent response formats

## Architecture Patterns

### 1. Provider Abstraction Layer
\`\`\`typescript
interface AIProvider {
  generateText(prompt: string, options?: any): Promise<string>;
  generateEmbedding(text: string): Promise<number[]>;
  analyzeImage(image: Buffer): Promise<any>;
}

class OpenAIProvider implements AIProvider {
  async generateText(prompt: string, options?: any) {
    const response = await openai.chat.completions.create({
      model: options?.model || 'gpt-4',
      messages: [{ role: 'user', content: prompt }]
    });
    return response.choices[0].message.content;
  }
}

class GeminiProvider implements AIProvider {
  async generateText(prompt: string, options?: any) {
    const model = genAI.getGenerativeModel({ 
      model: options?.model || 'gemini-2.5-flash' 
    });
    const result = await model.generateContent(prompt);
    return result.response.text();
  }
}

class ClaudeProvider implements AIProvider {
  async generateText(prompt: string, options?: any) {
    const response = await anthropic.messages.create({
      model: options?.model || 'claude-sonnet-4-20250514',
      messages: [{ role: 'user', content: prompt }]
    });
    return response.content[0].text;
  }
}
\`\`\`

### 2. Intelligent Router
\`\`\`typescript
class AIRouter {
  private providers: Map<string, AIProvider>;
  
  constructor() {
    this.providers = new Map([
      ['openai', new OpenAIProvider()],
      ['gemini', new GeminiProvider()],
      ['claude', new ClaudeProvider()]
    ]);
  }
  
  async route(request: AIRequest): Promise<string> {
    const provider = this.selectProvider(request);
    
    try {
      return await provider.generateText(request.prompt, request.options);
    } catch (error) {
      return await this.fallback(request, provider);
    }
  }
  
  private selectProvider(request: AIRequest): AIProvider {
    // Cost-based routing
    if (request.priority === 'cost') {
      return this.providers.get('gemini')!;
    }
    
    // Performance-based routing
    if (request.priority === 'speed') {
      return this.providers.get('gemini')!; // Flash is fastest
    }
    
    // Quality-based routing
    if (request.complexity === 'high') {
      return this.providers.get('claude')!; // Best reasoning
    }
    
    return this.providers.get('gemini')!; // Default
  }
  
  private async fallback(request: AIRequest, failed: AIProvider) {
    const fallbackOrder = ['gemini', 'claude', 'openai'];
    
    for (const name of fallbackOrder) {
      const provider = this.providers.get(name);
      if (provider !== failed) {
        try {
          return await provider.generateText(request.prompt);
        } catch (error) {
          continue;
        }
      }
    }
    
    throw new Error('All providers failed');
  }
}
\`\`\`

## Cost Optimization

### Request Analysis
\`\`\`typescript
class CostOptimizer {
  calculateCost(provider: string, inputTokens: number, outputTokens: number) {
    const pricing = {
      'gemini-flash': { input: 0.075, output: 0.30 },
      'gemini-pro': { input: 1.25, output: 5.00 },
      'gpt-5-mini': { input: 0.15, output: 0.60 },
      'gpt-5': { input: 5.00, output: 15.00 },
      'claude-sonnet': { input: 3.00, output: 15.00 }
    };
    
    const rates = pricing[provider];
    return (inputTokens * rates.input + outputTokens * rates.output) / 1000000;
  }
  
  selectCheapestProvider(estimatedTokens: number, quality: 'low' | 'medium' | 'high') {
    if (quality === 'low') return 'gemini-flash';
    if (quality === 'medium') return 'gemini-pro';
    return 'claude-sonnet';
  }
}
\`\`\`

## Load Balancing

### Round-Robin with Health Checks
\`\`\`typescript
class LoadBalancer {
  private providers: AIProvider[];
  private currentIndex = 0;
  private healthStatus = new Map<AIProvider, boolean>();
  
  async distribute(request: AIRequest): Promise<string> {
    const healthyProviders = this.providers.filter(p => 
      this.healthStatus.get(p) !== false
    );
    
    const provider = healthyProviders[this.currentIndex % healthyProviders.length];
    this.currentIndex++;
    
    try {
      const result = await provider.generateText(request.prompt);
      this.healthStatus.set(provider, true);
      return result;
    } catch (error) {
      this.healthStatus.set(provider, false);
      return this.distribute(request); // Retry with next provider
    }
  }
}
\`\`\`

## Rate Limit Management

\`\`\`typescript
class RateLimitManager {
  private limits = new Map<string, { count: number; resetAt: number }>();
  
  async checkAndWait(provider: string): Promise<void> {
    const limit = this.limits.get(provider);
    
    if (limit && limit.count >= this.getLimit(provider)) {
      const waitMs = limit.resetAt - Date.now();
      if (waitMs > 0) {
        await new Promise(resolve => setTimeout(resolve, waitMs));
        this.limits.set(provider, { count: 0, resetAt: Date.now() + 60000 });
      }
    }
    
    const current = this.limits.get(provider) || { count: 0, resetAt: Date.now() + 60000 };
    this.limits.set(provider, { ...current, count: current.count + 1 });
  }
  
  private getLimit(provider: string): number {
    return {
      'gemini': 60,
      'openai': 500,
      'claude': 50
    }[provider] || 100;
  }
}
\`\`\``,
      duration: '6 hours',
      quiz: {
        questions: [
          {
            id: 'plat1-q1',
            question: 'What is the primary benefit of a multi-provider AI architecture?',
            options: [
              'Lower latency',
              'Reliability and fallback options during outages',
              'Simpler code',
              'Better accuracy'
            ],
            correctAnswer: 1,
            explanation: 'Multi-provider architectures provide reliability through fallback mechanisms and avoid vendor lock-in.'
          },
          {
            id: 'plat1-q2',
            question: 'Which provider generally offers the lowest cost for high-volume requests?',
            options: ['OpenAI GPT-5', 'Claude Sonnet', 'Gemini Flash', 'All cost the same'],
            correctAnswer: 2,
            explanation: 'Gemini Flash typically has the lowest per-token cost among major providers.'
          }
        ]
      }
    },
    {
      id: 'platform-module-2',
      title: 'NVIDIA + Cloud AI Integration',
      description: 'Combine on-premise GPU with cloud services',
      content: `# NVIDIA + Cloud AI Integration

## Hybrid Architecture

### Use Cases
- **Training on Cloud**: Use cloud GPUs for training
- **Inference on Edge**: Deploy to Jetson for low-latency
- **Burst to Cloud**: Handle peak loads with cloud resources
- **Data Sovereignty**: Keep sensitive data on-premise

## On-Premise GPU + Cloud Pattern

\`\`\`python
class HybridInferenceSystem:
    def __init__(self):
        self.local_gpu = LocalGPUInference()
        self.cloud_api = CloudAIClient()
        
    async def process(self, request):
        # Check local GPU availability
        if self.local_gpu.is_available():
            try:
                return await self.local_gpu.infer(request)
            except Exception as e:
                logger.warning(f"Local GPU failed: {e}, falling back to cloud")
                
        # Fallback to cloud
        return await self.cloud_api.infer(request)
\`\`\`

## Model Serving with Triton

### Triton + Cloud Integration
\`\`\`yaml
# triton-config.yaml
models:
  - name: "local_resnet"
    platform: "tensorrt_plan"
    max_batch_size: 32
    
  - name: "cloud_llm"
    platform: "python"
    backend: "cloud_proxy"
\`\`\`

\`\`\`python
# Cloud proxy backend
import tritonclient.http as httpclient

class CloudProxyBackend:
    def execute(self, requests):
        results = []
        for request in requests:
            # Route to appropriate cloud provider
            if self.should_use_cloud(request):
                result = self.call_cloud_api(request)
            else:
                result = self.call_local_model(request)
            results.append(result)
        return results
\`\`\`

## Edge-to-Cloud Workflows

### Jetson Edge Processing
\`\`\`python
class EdgeCloudProcessor:
    def __init__(self):
        self.edge_model = load_tensorrt_model("edge_model.plan")
        self.cloud_client = OpenAI()
        
    async def process_video_stream(self, stream):
        # Process on edge for real-time detection
        detections = self.edge_model.detect(stream.frame)
        
        # Send to cloud for complex analysis
        if detections.confidence < 0.7:
            cloud_result = await self.cloud_client.analyze(
                image=stream.frame,
                prompt="Verify and describe this detection"
            )
            return cloud_result
            
        return detections
\`\`\`

## Cost-Performance Optimization

### Decision Matrix
\`\`\`python
class CostOptimizer:
    def decide_compute_location(self, task):
        # Calculate costs
        local_cost = self.estimate_local_cost(task)
        cloud_cost = self.estimate_cloud_cost(task)
        
        # Factor in latency requirements
        if task.latency_requirement < 100:  # ms
            return "local"  # Must be local for real-time
            
        # Factor in data transfer costs
        if task.data_size_mb > 100:
            return "local"  # Expensive to transfer
            
        # Choose cheaper option
        return "local" if local_cost < cloud_cost else "cloud"
    
    def estimate_local_cost(self, task):
        # GPU depreciation + power
        gpu_hour_cost = 0.50  # On-premise A100
        processing_hours = task.estimated_time / 3600
        return gpu_hour_cost * processing_hours
    
    def estimate_cloud_cost(self, task):
        # API call costs
        return task.estimated_tokens * 0.002 / 1000
\`\`\`

## Data Synchronization

\`\`\`python
class DataSyncManager:
    async def sync_model_updates(self):
        # Download updated models from cloud
        new_version = await self.cloud_storage.get_latest_model()
        
        if new_version > self.local_version:
            # Download and optimize for edge
            model_file = await self.download_model(new_version)
            optimized = self.optimize_for_jetson(model_file)
            self.deploy_to_edge(optimized)
            
    async def upload_edge_metrics(self):
        # Upload performance metrics to cloud
        metrics = self.collect_edge_metrics()
        await self.cloud_monitoring.send(metrics)
\`\`\``,
      duration: '6 hours',
      quiz: {
        questions: [
          {
            id: 'plat2-q1',
            question: 'When should you process AI inference on edge vs cloud?',
            options: [
              'Always use cloud for better accuracy',
              'Use edge for low-latency requirements, cloud for complex analysis',
              'Always use edge to save costs',
              'No difference between the two'
            ],
            correctAnswer: 1,
            explanation: 'Edge is ideal for low-latency real-time processing, while cloud handles complex, compute-intensive tasks.'
          }
        ]
      }
    },
    {
      id: 'platform-module-3',
      title: 'GitHub-Centric ML Workflows',
      description: 'Collaborative ML development with Git',
      content: `# GitHub-Centric ML Workflows

## Model Versioning with Git

### DVC (Data Version Control)
\`\`\`bash
# Initialize DVC
dvc init

# Track large files
dvc add data/train.csv
dvc add models/model.pkl

# Push to remote storage
dvc remote add -d storage s3://my-bucket/dvc
dvc push

# Git tracks .dvc files
git add data/train.csv.dvc models/model.pkl.dvc
git commit -m "Add training data and model"
\`\`\`

### Model Registry
\`\`\`python
# .github/workflows/register-model.yml
name: Register Model

on:
  push:
    tags:
      - 'model-v*'

jobs:
  register:
    runs-on: ubuntu-latest
    steps:
      - name: Download model artifact
        uses: actions/download-artifact@v3
        
      - name: Register in Hugging Face
        run: |
          python scripts/register_model.py \
            --model-path ./model \
            --version \${{ github.ref_name }}
\`\`\`

## Automated Retraining Pipelines

\`\`\`yaml
# .github/workflows/retrain.yml
name: Automated Retraining

on:
  schedule:
    - cron: '0 0 * * 0'  # Weekly
  workflow_dispatch:

jobs:
  retrain:
    runs-on: ubuntu-latest
    
    steps:
      - uses: actions/checkout@v3
      
      - name: Fetch latest data
        run: python scripts/fetch_data.py
        
      - name: Train model
        run: python train.py --config configs/prod.yaml
        
      - name: Evaluate model
        id: eval
        run: |
          python evaluate.py
          echo "accuracy=\$(cat metrics.json | jq .accuracy)" >> $GITHUB_OUTPUT
      
      - name: Deploy if improved
        if: steps.eval.outputs.accuracy > 0.90
        run: python scripts/deploy.py
        
      - name: Create PR if degraded
        if: steps.eval.outputs.accuracy < 0.85
        uses: peter-evans/create-pull-request@v5
        with:
          title: "Model Performance Degraded"
          body: "Accuracy dropped to \${{ steps.eval.outputs.accuracy }}"
\`\`\`

## Collaborative Development

### Pull Request Checks
\`\`\`yaml
# .github/workflows/pr-checks.yml
name: ML PR Checks

on:
  pull_request:
    paths:
      - 'models/**'
      - 'notebooks/**'

jobs:
  validate:
    runs-on: ubuntu-latest
    steps:
      - name: Run model tests
        run: pytest tests/model_tests.py
        
      - name: Check model size
        run: |
          size=\$(du -sm models/model.pkl | cut -f1)
          if [ $size -gt 100 ]; then
            echo "Model too large: \${size}MB"
            exit 1
          fi
      
      - name: Validate metrics
        run: python scripts/validate_metrics.py
        
      - name: Comment results
        uses: actions/github-script@v6
        with:
          script: |
            github.rest.issues.createComment({
              issue_number: context.issue.number,
              owner: context.repo.owner,
              repo: context.repo.repo,
              body: 'Model validation passed ✅'
            })
\`\`\`

## Experiment Tracking

\`\`\`python
import wandb
import os

# Initialize tracking
wandb.init(
    project="my-ml-project",
    name=f"run-{os.environ.get('GITHUB_RUN_ID')}",
    tags=[os.environ.get('GITHUB_REF_NAME')]
)

# Log hyperparameters
wandb.config.update({
    "learning_rate": 0.001,
    "epochs": 10,
    "batch_size": 32
})

# Log metrics during training
for epoch in range(epochs):
    wandb.log({
        "train_loss": train_loss,
        "val_accuracy": val_acc
    })

# Save model artifact
wandb.save("model.pkl")
\`\`\`

## Open Source ML Management

### GitHub Releases for Models
\`\`\`bash
# Create release with model
gh release create model-v1.0.0 \
  model.pkl \
  --title "Production Model v1.0.0" \
  --notes "Accuracy: 92.5%, trained on 1M samples"

# Download specific version
gh release download model-v1.0.0 -p "model.pkl"
\`\`\``,
      duration: '5 hours',
      quiz: {
        questions: [
          {
            id: 'plat3-q1',
            question: 'What is DVC (Data Version Control) used for?',
            options: [
              'Version control for code only',
              'Version control for large data files and models',
              'Database management',
              'API versioning'
            ],
            correctAnswer: 1,
            explanation: 'DVC tracks large files like datasets and models, working alongside Git for complete ML project versioning.'
          }
        ]
      }
    },
    {
      id: 'platform-module-4',
      title: 'Production AI System Design',
      description: 'Monitoring, observability, and disaster recovery',
      content: `# Production AI System Design

## Monitoring & Observability

### Metrics Collection
\`\`\`typescript
class AIMetrics {
  private prometheus: PrometheusClient;
  
  trackRequest(provider: string, duration: number, tokens: number, cost: number) {
    // Request metrics
    this.prometheus.histogram('ai_request_duration_ms', duration, { provider });
    this.prometheus.counter('ai_tokens_used', tokens, { provider });
    this.prometheus.counter('ai_cost_usd', cost, { provider });
  }
  
  trackError(provider: string, errorType: string) {
    this.prometheus.counter('ai_errors_total', 1, { provider, error_type: errorType });
  }
  
  trackModelPerformance(modelId: string, accuracy: number, latency: number) {
    this.prometheus.gauge('model_accuracy', accuracy, { model_id: modelId });
    this.prometheus.histogram('model_latency_ms', latency, { model_id: modelId });
  }
}
\`\`\`

### Distributed Tracing
\`\`\`typescript
import { trace } from '@opentelemetry/api';

async function processAIRequest(request: AIRequest) {
  const span = trace.getTracer('ai-service').startSpan('ai_request');
  
  try {
    span.setAttribute('provider', request.provider);
    span.setAttribute('model', request.model);
    
    const result = await router.route(request);
    
    span.setAttribute('tokens_used', result.usage.total_tokens);
    span.setStatus({ code: SpanStatusCode.OK });
    
    return result;
  } catch (error) {
    span.recordException(error);
    span.setStatus({ code: SpanStatusCode.ERROR });
    throw error;
  } finally {
    span.end();
  }
}
\`\`\`

## A/B Testing & Experimentation

\`\`\`typescript
class ABTestManager {
  async selectVariant(userId: string, experiment: string) {
    const hash = this.hashUserId(userId);
    const variant = hash % 100 < 50 ? 'control' : 'treatment';
    
    await this.trackAssignment(userId, experiment, variant);
    return variant;
  }
  
  async getModelForUser(userId: string) {
    const variant = await this.selectVariant(userId, 'model_comparison');
    
    return variant === 'control' 
      ? 'gemini-2.5-flash'
      : 'claude-sonnet-4-20250514';
  }
  
  async recordOutcome(userId: string, experiment: string, metric: number) {
    await this.analytics.track({
      user_id: userId,
      experiment,
      metric,
      timestamp: Date.now()
    });
  }
}
\`\`\`

## Security & Compliance

### API Key Rotation
\`\`\`typescript
class SecretManager {
  private secrets: Map<string, { value: string; expiresAt: number }>;
  
  async rotateKeys() {
    for (const [provider, secret] of this.secrets) {
      if (Date.now() > secret.expiresAt) {
        const newKey = await this.generateNewKey(provider);
        await this.updateSecret(provider, newKey);
        await this.notifyRotation(provider);
      }
    }
  }
  
  async getKey(provider: string): Promise<string> {
    const secret = this.secrets.get(provider);
    if (!secret || Date.now() > secret.expiresAt) {
      await this.rotateKeys();
    }
    return this.secrets.get(provider)!.value;
  }
}
\`\`\`

### Data Privacy
\`\`\`typescript
class PrivacyManager {
  async sanitizeRequest(request: AIRequest): Promise<AIRequest> {
    // Remove PII before sending to AI provider
    const sanitized = { ...request };
    sanitized.prompt = this.removePII(request.prompt);
    
    // Log sanitization
    await this.auditLog.record({
      action: 'sanitize',
      original_length: request.prompt.length,
      sanitized_length: sanitized.prompt.length
    });
    
    return sanitized;
  }
  
  private removePII(text: string): string {
    return text
      .replace(/\b[\w\.-]+@[\w\.-]+\.\w+\b/g, '[EMAIL]')
      .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN]')
      .replace(/\b\d{10,16}\b/g, '[CARD]');
  }
}
\`\`\`

## Disaster Recovery

### Circuit Breaker Pattern
\`\`\`typescript
class CircuitBreaker {
  private state: 'closed' | 'open' | 'half-open' = 'closed';
  private failureCount = 0;
  private lastFailureTime = 0;
  
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailureTime > 60000) {
        this.state = 'half-open';
      } else {
        throw new Error('Circuit breaker is open');
      }
    }
    
    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }
  
  private onSuccess() {
    this.failureCount = 0;
    this.state = 'closed';
  }
  
  private onFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    if (this.failureCount >= 5) {
      this.state = 'open';
    }
  }
}
\`\`\`

### Backup & Failover
\`\`\`typescript
class DisasterRecovery {
  private primaryProvider: AIProvider;
  private backupProvider: AIProvider;
  
  async processWithFailover(request: AIRequest): Promise<string> {
    try {
      return await this.primaryProvider.generateText(request.prompt);
    } catch (error) {
      await this.notifyFailover('primary_down');
      
      try {
        return await this.backupProvider.generateText(request.prompt);
      } catch (backupError) {
        await this.notifyFailover('all_providers_down');
        throw new Error('All providers unavailable');
      }
    }
  }
}
\`\`\``,
      duration: '5 hours',
      quiz: {
        questions: [
          {
            id: 'plat4-q1',
            question: 'What is the purpose of a circuit breaker in AI systems?',
            options: [
              'To reduce electricity costs',
              'To prevent cascading failures by stopping requests to failing services',
              'To improve model accuracy',
              'To encrypt API keys'
            ],
            correctAnswer: 1,
            explanation: 'Circuit breakers prevent system overload by stopping requests to failing services until they recover.'
          },
          {
            id: 'plat4-q2',
            question: 'Why is PII sanitization important before sending data to AI providers?',
            options: [
              'To improve model accuracy',
              'To reduce token costs',
              'To protect user privacy and comply with regulations',
              'To speed up processing'
            ],
            correctAnswer: 2,
            explanation: 'Sanitizing PII protects user privacy and ensures compliance with regulations like GDPR and CCPA.'
          }
        ]
      }
    }
  ]
};
